import {Injectable} from "@angular/core";
import {BehaviorSubject, combineLatest, Observable} from "rxjs";
import {filter, map, switchMap, take} from "rxjs/operators";
import {notNull} from "@app/core/shared/domain/type.utils";
import {GetCartLineQuantityUseCase} from "@app/core/cart/application/get-cart-line-quantity.use-case";
import {RemoveProductUseCase} from "@app/core/cart/application/remove-product.use-case";
import {RemovePromotionUseCase} from "@app/core/cart/application/remove-promotion.use-case";
import {Product} from "@app/core/product/domain/dtos/product";

@Injectable()
export class RemoveCartLineFromCartViewModel {

    private productSubject = new BehaviorSubject<Product | null>(null)
    private product$: Observable<Product> = this.productSubject.asObservable().pipe(
        filter(notNull)
    );

    private promotionCodeSubject = new BehaviorSubject<string | null>(null);
    private promotionCode$: Observable<string | null> = this.promotionCodeSubject.asObservable();

    private cartLineQuantity$: Observable<number> = combineLatest([
        this.product$.pipe(map(product => product.code)),
        this.promotionCode$,
    ]).pipe(
        switchMap(([productCode, promotionCode]) => {
            if (promotionCode) {
                return this.getCartLineQuantityUseCase.execute(productCode, promotionCode);
            }
            return this.getCartLineQuantityUseCase.execute(productCode, "");
        }),
    );

    readonly isCartLineInCart$: Observable<boolean> = this.cartLineQuantity$.pipe(
        map(quantity => quantity > 0),
    )

    constructor(
        private getCartLineQuantityUseCase: GetCartLineQuantityUseCase,
        private removeProductUseCase: RemoveProductUseCase,
        private removePromotionUseCase: RemovePromotionUseCase
    ) {
    }

    setProductCode(product: Product) {
        this.productSubject.next(product);
    }

    setPromotionCode(productCode: string) {
        this.promotionCodeSubject.next(productCode);
    }

    removeCartLine() {
        const product = this.productSubject.value;
        const promotionCode = this.promotionCodeSubject.value;

        if (promotionCode !== null) {
            this.removePromotionUseCase.execute(promotionCode)
                .pipe(take(1))
                .subscribe()
            return;
        }
        if (product !== null) {
            this.removeProductUseCase.execute(product)
                .pipe(take(1))
                .subscribe();
        }
    }

}
